import { Inject, Injectable, forwardRef, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Http, Headers, Response } from '@angular/http';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import 'rxjs/add/operator/catch';

import { InterfaceService } from '@app/interfaces';
import { setToken, getToken, unsetToken, getTokenExpiration, isTokenExpired, decodeToken } from './token';
import { User } from '@app/models';
import { SettingsService } from '@app/core';
import { NavigationControlService } from '@app/shared/directives/custom-devops-header/navigation-control.service';


@Injectable()
export class AuthService extends InterfaceService {
    user: User;
    userPermissions: Array<string> = [];
    isAdmin = false;
    applicationId: any;

    constructor(
        private http: HttpClient,
        private router: Router,
        private navigateService: NavigationControlService,
        @Inject(forwardRef(() => SettingsService)) private settings,
    ) {
        super();

        this.subscriptions['user'] = this.settings.user$.subscribe(u => this.user = u);
        this.subscriptions['userPermissions'] = this.settings.user$.subscribe((ps: User) => this.userPermissions = ps['accesses']);
        this.navigateService.getApplicationId.subscribe(applicationId => {
            this.applicationId = applicationId;
        });
    }

    // Checks is authenticated
    public authenticated() {
        // Check if there's an unexpired JWT
        return !isTokenExpired();
    }

    // Checks is user has roles
    public permitted(roles: Array<string>, userType: any, rolesAnyOne: boolean = false) {

        if (!(this.user instanceof Object) || !(this.userPermissions instanceof Array)) {
            return false;
        } else {
            return rolesAnyOne ? intersect(this.userPermissions, roles).length > 0 : intersect(this.userPermissions, roles).length === roles.length;
        }


        function intersect(a, b) {
            let t;
            if (b.length > a.length) {
                t = b, b = a, a = t;
            } // indexOf to loop over shorter
            return a.filter(e => b.indexOf(e) > -1).filter((e, i, c) => c.indexOf(e) === i); // extra step to remove duplicates
        }
    }

    // Login action
    public login(email: string, password: string) {
        const credentials = `{"email":"${email}","password":"${password}"}`;

        return this.http.post(this.getApiUrl('api-token-auth/'), credentials, this.getHttpOptions('json', false))
            .pipe(
                tap(response => this.handleAuthResponse(response)),
                catchError(this.handleError)
            );
    }

    // Refreshing token
    public refreshToken() {
        console.log('Token expired refreshing : ', new Date());
        return this.http.get(this.getApiUrl('api-token-refresh/'), this.getHttpOptions('json', false))
            .pipe(
                tap(response => this.handleAuthResponse(response[0])),
                catchError(error => {
                    error = this.handleError(error);
                    this.router.navigate(['/auth/login']);
                    return error;
                })
            );
    }

    // Logout action
    public logout() {
        if (getToken()) {
            unsetToken();
            this.settings.setUser();
        }

        localStorage.clear();
        this.router.navigate(['/auth/login']);
        // location.href='/';
    }

    //signup
    signup(data) {
        return this.http.post(this.getApiUrl(`api-signup/`), JSON.stringify(data), this.getHttpOptions())
            .pipe(
                tap(Res => Res),
                catchError(this.handleError)
            );
    }

    // API call to validate user details and generate password reset link
    generatePasswordResetLink(email: string) {
        const data = {
            'email': email,
            'base_path': window.location.origin + this.router.createUrlTree(['/auth/password/reset/confirm/']).toString()
        };


        return this.http
            .post(this.getApiUrl(`api-password-reset/`), JSON.stringify(data), this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }

    // API call to verify password reset key
    verifyPasswordResetToken(token: string) {

        const data = {
            'token': token,
        };

        return this.http
            .post(this.getApiUrl(`api-password-reset-verify/`), JSON.stringify(data), this.getHttpOptions())
            .pipe(catchError(this.handleError));
    }

    // API call to reset password
    resetPassword(token: string, password: string) {
        const data = {
            'password': password,
        };
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `JWT ${token}`);
        return this.http
            .post(this.getApiUrl(`api-password-reset-confirm/`), JSON.stringify(data), { headers: headers })
            .pipe(catchError(this.handleError));

    }

    // Handle response and schedule token auto refresh
    public handleAuthResponse(response: any): void {
        setToken(response['token']);
        this.settings.setUser(response['user']);

        const now = new Date().valueOf();
        const exp = <Date>getTokenExpiration();
        const delay = exp.valueOf() - now;

        const timer = Observable.timer(delay - 60);

        timer.subscribe(() => {
            this.refreshToken().subscribe();
        });
    }

    // Redirecting to respective homepages for the recpective user types
    redirectToHome(returnUrl?: any) {
        this.settings.user$.subscribe(res => {
            if (res['isSuperUser']) {
                this.router.navigate([returnUrl || '/admin/customer']);
            } else if (res['isSupportUser'] && !res['isCustomerPortal']) {
                this.router.navigate([returnUrl || '/support/tickets'])
            }
            else {
                if (res['latest_app']) {
                    // if (this.applicationId) {
                    //     this.router.navigate([`/applications/${this.applicationId}/dashboard`]);
                    // } else {
                    this.router.navigate([returnUrl || `/applications/${res['latest_app']}/dashboard`]);
                    // }
                } else {
                    this.router.navigate([returnUrl || '/applications/dashboard']);
                }
            }
        });
    }

    adminLogin(data) {
        return this.http.post(this.getApiUrl('user/admin-login/'), JSON.stringify(data), this.getHttpOptions('json', false))
            .pipe(
                tap(response => this.handleAuthResponse(response)),
                catchError(this.handleError)
            );
    }

    checkAdmin() {
        return this.user['isAdmin'];
    }

    isLoggedIn() {
        return this.authenticated();
    }

    get currentUser() {
        let token = getToken();
        if (!token) return null;

        return decodeToken(token)
    }
}
